import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  "Medical Affairs",
  "HEOR",
  "Market Access",
  "HTA / JCA",
  "AMCP / Payer",
  "Patient Voice / RWE",
  "Brand / Launch Strategy",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    interest_area: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      track("briefing_request_submitted", "/contact", { interest_area: form.interest_area || null });
      setSubmitted(true);
      toast.success("Briefing request received — we’ll be in touch shortly.");
      setForm({ name: "", email: "", company: "", role: "", interest_area: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-ink-0 relative overflow-hidden">
        <div className="absolute inset-0 glow-rust opacity-90 pointer-events-none" aria-hidden="true" />
        <div className="container-edge pt-20 lg:pt-28 pb-20 relative z-10">
          <div className="grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 lg:col-span-6">
              <div className="eyebrow">Request a Briefing</div>
              <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-[4.8rem] leading-[1.05] tracking-tight mt-4 text-parchment-bright">
                A 45-minute strategic walkthrough.
                <span className="block italic text-parchment">Confidential. Tailored to your cycle.</span>
              </h1>
              <p className="mt-6 text-parchment/70 text-lg leading-relaxed max-w-md">
                Share where your asset is in the lifecycle. We&rsquo;ll show you the patient signal landscape, the evidence gaps, and the actionable next step.
              </p>
              <div className="mt-12 border-t border-white/[0.08] pt-8">
                <div className="eyebrow-muted mb-4">For functions across</div>
                <ul className="grid grid-cols-2 gap-y-2.5 text-[15px] text-parchment/85">
                  {INTEREST_OPTIONS.map((o) => (
                    <li key={o} className="flex items-start gap-2">
                      <span className="text-rust mt-1.5 text-[8px]">●</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 border-t border-white/[0.08] pt-8">
                <div className="eyebrow-muted mb-3">Or write to us directly</div>
                <a
                  href="mailto:enquiry@marksmanhealthcare.com?subject=Voice2Evidence%20Briefing%20Request"
                  className="font-serif text-2xl md:text-3xl text-parchment-bright hover:text-rust transition-colors"
                  data-testid="contact-email-link"
                >
                  enquiry@marksmanhealthcare.com
                </a>
                <p className="text-sm text-parchment/60 mt-3 leading-relaxed">
                  All correspondence is reviewed by a senior expert team.
                </p>
              </div>
            </Reveal>

            <Reveal className="col-span-12 lg:col-span-6" delay={120}>
              <div className="card-dark p-8 lg:p-10">
                {submitted ? (
                  <div className="py-10" data-testid="contact-success">
                    <div className="eyebrow mb-4">Received</div>
                    <h2 className="font-serif text-3xl md:text-4xl text-parchment-bright">Thank you.</h2>
                    <p className="mt-4 text-parchment/70 leading-relaxed">
                      Your request is logged. A member of our team will respond within one business day with a proposed briefing window.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-rust hover:text-terracotta transition-colors"
                      data-testid="contact-send-another"
                    >
                      ← Submit another request
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
                    <Field id="name" label="Full Name" required value={form.name} onChange={update("name")} />
                    <Field id="email" label="Work Email" type="email" required value={form.email} onChange={update("email")} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Field id="company" label="Company" required value={form.company} onChange={update("company")} />
                      <Field id="role" label="Role / Title" value={form.role} onChange={update("role")} />
                    </div>

                    <div>
                      <label htmlFor="interest_area" className="block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-on-dark mb-2">
                        Area of Interest
                      </label>
                      <select
                        id="interest_area"
                        value={form.interest_area}
                        onChange={update("interest_area")}
                        className="w-full bg-transparent border-b border-white/[0.15] rounded-none px-0 py-3 text-parchment focus:border-rust focus:outline-none focus:ring-0 transition-colors font-sans"
                        data-testid="contact-input-interest"
                      >
                        <option value="" className="bg-ink-2">Select an area…</option>
                        {INTEREST_OPTIONS.map((o) => (
                          <option key={o} value={o} className="bg-ink-2">{o}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-on-dark mb-2">
                        What are you working on?
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        required
                        value={form.message}
                        onChange={update("message")}
                        placeholder="Therapeutic area, lifecycle stage, current challenge…"
                        className="w-full bg-transparent border-b border-white/[0.15] rounded-none px-0 py-3 text-parchment focus:border-rust focus:outline-none focus:ring-0 transition-colors font-sans placeholder:text-parchment/30 resize-none"
                        data-testid="contact-input-message"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-on-dark">
                        Confidential · Reviewed by senior expert team
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="btn-accent disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="contact-submit-button"
                      >
                        {submitting ? "Sending…" : "Submit Request"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required = false }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-on-dark mb-2">
        {label}{required && <span className="text-rust ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-white/[0.15] rounded-none px-0 py-3 text-parchment focus:border-rust focus:outline-none focus:ring-0 transition-colors font-sans"
        data-testid={`contact-input-${id}`}
      />
    </div>
  );
}
